import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import { auth } from '../utils/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('This reset link is missing its token. Please request a new one.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== password.trim()) {
      setError('Password must not begin or end with a space. Please remove any leading or trailing spaces.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await auth.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicNav />

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-2">Reset Password</p>
            <h1 className="font-serif text-2xl text-navy-900">Set a new password</h1>
            <p className="text-gray-500 text-sm mt-1">Choose a new password for your account</p>
          </div>

          <div className="border border-gray-200 rounded-lg p-8">
            {done ? (
              <div className="bg-green-50 border border-green-200 text-green-700 rounded px-4 py-3 text-sm">
                Password updated! Redirecting you to sign in…
              </div>
            ) : (
              <>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded px-4 py-3 mb-4 text-sm">
                    {error}
                  </div>
                )}
                {!token && (
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded px-4 py-3 mb-4 text-sm">
                    No reset token found in this link.{' '}
                    <Link to="/login" className="underline">Request a new one</Link>.
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-gray-600 text-sm mb-1 block">New Password</label>
                    <div className="relative">
                      <input
                        type={showPass ? 'text' : 'password'}
                        value={password}
                        autoComplete="new-password"
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-white border border-gray-300 rounded px-4 py-3 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600 transition-colors pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPass(!showPass)}
                        className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                      >
                        {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-gray-400 text-xs mt-1">At least 8 characters</p>
                  </div>

                  <div>
                    <label className="text-gray-600 text-sm mb-1 block">Confirm New Password</label>
                    <div className="relative">
                      <input
                        type={showConfirm ? 'text' : 'password'}
                        value={confirm}
                        autoComplete="new-password"
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="••••••••"
                        className={`w-full bg-white border rounded px-4 py-3 text-navy-900 placeholder-gray-400 focus:outline-none transition-colors pr-10 ${
                          confirm && confirm !== password
                            ? 'border-red-400 focus:border-red-500'
                            : 'border-gray-300 focus:border-navy-600'
                        }`}
                      />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600" tabIndex="-1">
                        {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {confirm && confirm !== password && (
                      <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || !token}
                    className="w-full bg-navy-900 hover:bg-navy-800 disabled:opacity-60 text-white font-medium py-3 rounded transition-colors mt-2"
                  >
                    {submitting ? 'Please wait…' : 'Update Password'}
                  </button>
                </form>
              </>
            )}
          </div>

          <p className="text-center mt-6">
            <Link to="/login" className="text-gray-500 text-sm hover:text-navy-900 transition-colors">← Back to sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
